import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Unit, Event, Group, DateFilter, DeleteConfirmation, GroupMember, UnitMember } from '../types';
import {
  LogOut,
  Download,
  Trash2,
  Monitor,
  Activity,
  Folder,
  User as UserIcon,
  Mail,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowUpDown,
  Building2,
  RefreshCw,
  Plus,
  X,
  FolderPlus,
  ArrowRight,
  MinusCircle,
  Users,
  Pencil,
  Check,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import DateFilterComponent from './DateFilter';
import ConfirmDialog from './ConfirmDialog';
import CompanyManagement from './CompanyManagement';
import {
  exportUserData,
  exportUnitsData,
  exportEventsData,
  exportGroupsData,
  exportAllData
} from '../utils/exportUtils';
import { getEventDescription, getEventUserDisplay } from '../utils/eventUtils';
import { formatLockMode } from '../utils/lockModeUtils';


type SortConfig<T> = {
  key: keyof T;
  direction: 'ascending' | 'descending';
};

type ActiveTab = 'dashboard' | 'companies';

const Dashboard: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [units, setUnits] = useState<Unit[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ startDate: null, endDate: null });
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');

  // States for table sorting and filtering
  const [unitFilter, setUnitFilter] = useState('');
  const [unitSortConfig, setUnitSortConfig] = useState<SortConfig<Unit> | null>(null);
  const [eventFilter, setEventFilter] = useState('');
  const [eventSortConfig, setEventSortConfig] = useState<SortConfig<Event> | null>(null);
  const [groupFilter, setGroupFilter] = useState('');
  const [groupSortConfig, setGroupSortConfig] = useState<SortConfig<Group> | null>(null);

  // States for event pagination
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 10;

  // Create group modal
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createGroupLoading, setCreateGroupLoading] = useState(false);

  // Manage group locks modal
  const [managingGroup, setManagingGroup] = useState<Group | null>(null);
  const [groupLockFilter, setGroupLockFilter] = useState('');

  // Group members (sharing) modal
  const [viewingMembersGroup, setViewingMembersGroup] = useState<Group | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Unit members (sharing) modal
  const [viewingMembersUnit, setViewingMembersUnit] = useState<Unit | null>(null);
  const [viewingDetailsUnit, setViewingDetailsUnit] = useState<Unit | null>(null);
  const [unitMembers, setUnitMembers] = useState<UnitMember[]>([]);
  const [unitMembersLoading, setUnitMembersLoading] = useState(false);

  // Editable user name
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Refreshing
  const [refreshing, setRefreshing] = useState(false);

  const [confirmation, setConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {}
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [unitsResponse, groupsResponse] = await Promise.all([
        apiService.getUnits(),
        apiService.getGroups()
      ]);

      if (unitsResponse.success && unitsResponse.data) {
        setUnits(unitsResponse.data);
      }
      if (groupsResponse.success && groupsResponse.data) {
        // Units ya vienen incluidas desde el backend
        setGroups(groupsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      // Only load events when a unit is selected
      if (!selectedUnitId) {
        setEvents([]);
        return;
      }

      const startDate = dateFilter.startDate?.toISOString();
      const endDate = dateFilter.endDate?.toISOString();

      const eventsResponse = await apiService.getEvents(selectedUnitId, startDate, endDate);
      if (eventsResponse.success && eventsResponse.data) {
        // Filter events by unit and date
        let filteredEvents = eventsResponse.data.filter(event => event.unit_id === selectedUnitId);
        
        // Filter by date when date filters are selected
        if (dateFilter.startDate || dateFilter.endDate) {
          filteredEvents = filteredEvents.filter(event => {
            const eventDate = new Date(event.registered_at);
            const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
            const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;
            
            // If there is a start date, the event must be on or after it
            if (startDate && eventDate < startDate) {
              return false;
            }
            
            // If there is an end date, the event must be on or before it
            if (endDate && eventDate > endDate) {
              return false;
            }
            
            return true;
          });
        }
        
        setEvents(filteredEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  }, [selectedUnitId, dateFilter.startDate, dateFilter.endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set initial unit selection when units are loaded
  useEffect(() => {
    if (units.length > 0 && !selectedUnitId) {
      setSelectedUnitId(units[0].id);
    }
  }, [units, selectedUnitId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Monitor events state changes
  useEffect(() => {
  }, [events]);

  // Memoized and sorted data
  const sortedAndFilteredUnits = React.useMemo(() => {
    let filtered = units.filter(unit =>
      Object.values(unit).some(val =>
        String(val).toLowerCase().includes(unitFilter.toLowerCase())
      )
    );

    if (unitSortConfig !== null) {
      filtered.sort((a, b) => {
        const aValue = a[unitSortConfig.key];
        const bValue = b[unitSortConfig.key];
        if (aValue == null) return unitSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return unitSortConfig.direction === 'ascending' ? 1 : -1;
        if (aValue < bValue) {
          return unitSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return unitSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [units, unitFilter, unitSortConfig]);

  const requestSort = <T,>(key: keyof T, config: SortConfig<T> | null, setConfig: (config: SortConfig<T>) => void) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (config && config.key === key && config.direction === 'ascending') {
      direction = 'descending';
    }
    setConfig({ key, direction });
  };

  const sortedAndFilteredEvents = React.useMemo(() => {
    let filtered = events.filter(event =>
      Object.values(event).some(val =>
        String(val).toLowerCase().includes(eventFilter.toLowerCase())
      )
    );

    if (eventSortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (eventSortConfig.key === 'type') {
          aValue = getEventDescription(a);
          bValue = getEventDescription(b);
        } else {
          aValue = a[eventSortConfig.key];
          bValue = b[eventSortConfig.key];
        }

        if (aValue == null) return eventSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return eventSortConfig.direction === 'ascending' ? 1 : -1;
        
        if (aValue < bValue) {
          return eventSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return eventSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [events, eventFilter, eventSortConfig]);

  const sortedAndFilteredGroups = React.useMemo(() => {
    let filtered = groups.filter(group =>
      Object.values(group).some(val =>
        String(val).toLowerCase().includes(groupFilter.toLowerCase())
      )
    );

    if (groupSortConfig !== null) {
      filtered.sort((a, b) => {
        const aValue = a[groupSortConfig.key];
        const bValue = b[groupSortConfig.key];
        if (aValue == null) return groupSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return groupSortConfig.direction === 'ascending' ? 1 : -1;
        if (typeof aValue === 'object' || typeof bValue === 'object') return 0;
        if (aValue < bValue) {
          return groupSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return groupSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [groups, groupFilter, groupSortConfig]);


  const handleLogout = async () => {
    await logout();
  };

  const showConfirmation = (title: string, message: string, onConfirm: () => void) => {
    setConfirmation({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  const handleDeleteUnit = (unitId: string, unitName: string) => {
    showConfirmation(
      'Delete Unit',
      `Are you sure you want to delete the unit "${unitName}"? This action cannot be undone.`,
      async () => {
        try {
          await apiService.deleteUnit(unitId);
          setUnits(units.filter(u => u.id !== unitId));
        } catch (error) {
          console.error('Error deleting unit:', error);
        }
      }
    );
  };

  const handleDeleteEvents = () => {
    if (!selectedUnitId) {
      alert('Please select a unit first');
      return;
    }

    const startDate = dateFilter.startDate?.toISOString();
    const endDate = dateFilter.endDate?.toISOString();
    const selectedUnit = units.find(u => u.id === selectedUnitId);
    const unitName = selectedUnit ? selectedUnit.name : 'selected unit';
    
    const dateRange = startDate && endDate 
      ? `from ${format(new Date(startDate), 'dd/MM/yyyy', { locale: enUS })} to ${format(new Date(endDate), 'dd/MM/yyyy', { locale: enUS })}`
      : startDate 
        ? `from ${format(new Date(startDate), 'dd/MM/yyyy', { locale: enUS })}`
        : endDate
          ? `until ${format(new Date(endDate), 'dd/MM/yyyy', { locale: enUS })}`
          : 'all events';

    showConfirmation(
      'Delete Events',
      `Are you sure you want to delete ${dateRange} for unit "${unitName}"? This action cannot be undone.`,
      async () => {
        try {
          // Only delete events from the selected unit
          await apiService.deleteEvents(selectedUnitId, startDate, endDate);
          await loadEvents();
        } catch (error) {
          console.error('Error deleting events:', error);
        }
      }
    );
  };

  // Functions to handle event pagination
  const totalPages = Math.ceil(sortedAndFilteredEvents.length / eventsPerPage);
  const startIndex = (currentPage - 1) * eventsPerPage;
  const endIndex = startIndex + eventsPerPage;
  const currentEvents = sortedAndFilteredEvents.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset page when events change
  useEffect(() => {
    setCurrentPage(1);
  }, [events.length, dateFilter, eventFilter]);

  const handleDeleteGroup = (groupId: string, groupName: string) => {
    showConfirmation(
      'Delete Group',
      `Are you sure you want to delete the group "${groupName}"? This action cannot be undone.`,
      async () => {
        try {
          await apiService.deleteGroup(groupId);
          setGroups(groups.filter(g => g.id !== groupId));
        } catch (error) {
          console.error('Error deleting group:', error);
        }
      }
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadEvents();
    setRefreshing(false);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreateGroupLoading(true);
    try {
      const res = await apiService.createGroup(newGroupName.trim());
      if (res.success) {
        setNewGroupName('');
        setShowCreateGroup(false);
        await loadData();
      }
    } catch (error) {
      console.error('Error creating group:', error);
    } finally {
      setCreateGroupLoading(false);
    }
  };

  const handleAddUnitToGroup = async (unitId: string, groupId: string) => {
    try {
      const res = await apiService.addUnitToGroup(unitId, groupId);
      if (res.success) {
        await loadData();
      }
    } catch (error) {
      console.error('Error adding unit to group:', error);
    }
  };

  const handleRemoveUnitFromGroup = async (unitId: string) => {
    try {
      const res = await apiService.removeUnitFromGroup(unitId);
      if (res.success) {
        await loadData();
      }
    } catch (error) {
      console.error('Error removing unit from group:', error);
    }
  };

  // ── Group Members (Sharing) ────────────────────────────────

  const loadGroupMembers = async (group: Group) => {
    setViewingMembersGroup(group);
    setMembersLoading(true);
    try {
      const res = await apiService.getGroupMembers(group.id);
      if (res.success && res.data) {
        setGroupMembers(res.data);
      } else {
        setGroupMembers([]);
      }
    } catch (error) {
      console.error('Error loading group members:', error);
      setGroupMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRevokeGroupMember = (group: Group, member: GroupMember) => {
    showConfirmation(
      'Revoke Access',
      `Are you sure you want to revoke "${member.user_full_name}" access to the group "${group.name}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await apiService.removeGroupMember(group.id, member.id);
          if (res.success) {
            setGroupMembers(prev => prev.filter(m => m.id !== member.id));
          }
        } catch (error) {
          console.error('Error revoking member:', error);
        }
      }
    );
  };

  // ── Unit Members (Sharing) ─────────────────────────────────

  const loadUnitMembers = async (unit: Unit) => {
    setViewingMembersUnit(unit);
    setUnitMembersLoading(true);
    try {
      const res = await apiService.getUnitMembers(unit.id);
      if (res.success && res.data) {
        setUnitMembers(res.data);
      } else {
        setUnitMembers([]);
      }
    } catch (error) {
      console.error('Error loading unit members:', error);
      setUnitMembers([]);
    } finally {
      setUnitMembersLoading(false);
    }
  };

  const handleRevokeUnitMember = (unit: Unit, member: UnitMember) => {
    showConfirmation(
      'Revoke Access',
      `Are you sure you want to revoke "${member.user_full_name}" access to the unit "${unit.name}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await apiService.removeUnitMember(unit.id, member.id);
          if (res.success) {
            setUnitMembers(prev => prev.filter(m => m.id !== member.id));
          }
        } catch (error) {
          console.error('Error revoking unit member:', error);
        }
      }
    );
  };

  // ── User Name Editing ──────────────────────────────────────

  const handleStartEditName = () => {
    setEditNameValue(user?.name || user?.full_name || '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editNameValue.trim()) return;
    setSavingName(true);
    try {
      const parts = editNameValue.trim().split(' ');
      const firstName = parts[0];
      const lastName = parts.slice(1).join(' ') || undefined;
      const res = await apiService.updateUserProfile({ first_name: firstName, last_name: lastName });
      if (res.success && res.data) {
        updateUser({ name: res.data.name, full_name: res.data.name });
      }
      setEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg shadow-indigo-500/30">
          <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
        </div>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-indigo-600"></div>
        <p className="text-sm font-medium text-slate-400 tracking-wide">Loading your data...</p>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog confirmation={confirmation} />
      <div className="min-h-screen bg-slate-50">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-indigo-600" />
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center py-4 gap-4 sm:gap-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl overflow-hidden shadow-md shadow-indigo-500/20">
                  <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="text-lg font-extrabold text-slate-900 tracking-tight">LOCKWASTE</span>
                  <span className="ml-2 text-xs font-semibold text-slate-400 uppercase tracking-widest hidden sm:inline">Dashboard</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center px-3 py-2 text-sm font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all border border-slate-200 hover:border-indigo-200 disabled:opacity-50"
                  title="Refresh data"
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <div className="flex items-center text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full border border-slate-200">
                  <UserIcon className="h-3.5 w-3.5 mr-2 text-indigo-500" />
                  <span>{user?.name || user?.full_name || 'User'}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 py-2 text-sm font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all border border-slate-200 hover:border-red-200"
                >
                  <LogOut className="h-4 w-4 mr-1.5" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-slate-200 sticky top-[65px] z-[9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-1 py-2" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  activeTab === 'dashboard'
                    ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <Monitor className="h-4 w-4" />
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('companies')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  activeTab === 'companies'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <Building2 className="h-4 w-4" />
                Companies
              </button>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Company Management Tab */}
          {activeTab === 'companies' && <CompanyManagement />}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (<>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all overflow-hidden">
              <div className="h-1 bg-blue-500" />
              <div className="p-6 flex items-center gap-5">
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex-shrink-0">
                  <Monitor className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Units</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-0.5">{units.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all overflow-hidden">
              <div className="h-1 bg-emerald-500" />
              <div className="p-6 flex items-center gap-5">
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex-shrink-0">
                  <Activity className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loaded Events</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-0.5">{events.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all overflow-hidden">
              <div className="h-1 bg-violet-500" />
              <div className="p-6 flex items-center gap-5">
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 flex-shrink-0">
                  <Folder className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Groups</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-0.5">{groups.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-indigo-500" />
                  User Information
                </h2>
                <button
                  onClick={() => user && exportUserData(user)}
                  className="w-full sm:w-auto flex justify-center items-center px-4 py-2 text-sm font-medium bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors shadow-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                    <UserIcon className="h-5 w-5 text-indigo-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-500">Name</p>
                    {editingName ? (
                      <div className="flex items-center gap-2 mt-0.5">
                        <input
                          type="text"
                          value={editNameValue}
                          onChange={(e) => setEditNameValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                          className="flex-1 px-2 py-1 text-sm font-semibold text-slate-900 bg-white border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          autoFocus
                        />
                        <button
                          onClick={handleSaveName}
                          disabled={savingName || !editNameValue.trim()}
                          className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Save"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingName(false)}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{user?.name || user?.full_name || 'No name'}</p>
                        <button
                          onClick={handleStartEditName}
                          className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit name"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                    <Mail className="h-5 w-5 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Email</p>
                    <p className="font-semibold text-slate-900">{user?.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Groups Section - ABOVE Units */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <Folder className="h-5 w-5 mr-2 text-purple-500" />
                  Groups ({groups.length})
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm hover:shadow"
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    New Group
                  </button>
                  <button
                    onClick={() => exportGroupsData(groups)}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-white text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors shadow-sm"
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    Export
                  </button>
                </div>
              </div>
            </div>
            {/* Create Group Inline */}
            {showCreateGroup && (
              <div className="px-6 py-4 border-b border-slate-100 bg-purple-50/30">
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-slate-600 mb-1.5">Group Name</label>
                    <input
                      type="text"
                      placeholder="Enter group name..."
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                    />
                  </div>
                  <button
                    onClick={handleCreateGroup}
                    disabled={createGroupLoading || !newGroupName.trim()}
                    className="px-4 py-2.5 text-sm font-semibold bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 shadow-sm"
                  >
                    {createGroupLoading ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => { setShowCreateGroup(false); setNewGroupName(''); }}
                    className="px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div className="p-4 border-b border-slate-100 bg-white">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Filter className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Filter groups..."
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('name', groupSortConfig, setGroupSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Name <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Units</th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Shared With</th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedAndFilteredGroups.map((group) => (
                    <tr key={group.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 px-6 text-sm font-semibold text-slate-800">
                        {group.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-slate-600">
                        {group.units && group.units.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {group.units.map((unit, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                {unit.name}
                                {group.is_own === 1 ? (
                                  <button
                                    onClick={() => handleRemoveUnitFromGroup(unit.id)}
                                    className="ml-0.5 hover:text-red-600 transition-colors"
                                    title={`Remove ${unit.name} from group`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                ) : null}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">No units</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm">
                        {group.is_own === 1 ? (
                          <button
                            onClick={() => loadGroupMembers(group)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                            title="View shared members"
                          >
                            <Users className="h-3.5 w-3.5" />
                            Members
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 italic">
                            <Users className="h-3 w-3" />
                            Shared with me
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm">
                        {group.is_own === 1 ? (
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => { setManagingGroup(group); setGroupLockFilter(''); }}
                              className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Manage locks in group"
                            >
                              <FolderPlus size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(group.id, group.name)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Group"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {sortedAndFilteredGroups.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">
                        No groups found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Group Members Modal */}
          {viewingMembersGroup && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewingMembersGroup(null)}>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-100 bg-blue-50/50 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    Shared With — {viewingMembersGroup.name}
                  </h3>
                  <button onClick={() => setViewingMembersGroup(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                    <X className="h-4 w-4 text-slate-500" />
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[60vh]">
                  {membersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-blue-600" />
                    </div>
                  ) : groupMembers.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">
                      This group is not shared with anyone
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {groupMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{member.user_full_name}</p>
                              <p className="text-xs text-slate-400">{member.email || 'No email'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {member.allow_settings_changes && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Settings
                              </span>
                            )}
                            <button
                              onClick={() => handleRevokeGroupMember(viewingMembersGroup, member)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Revoke access"
                            >
                              <MinusCircle size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Manage Group Locks Modal */}
          {managingGroup && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setManagingGroup(null)}>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-100 bg-purple-50/50 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center">
                    <FolderPlus className="h-4 w-4 mr-2 text-purple-500" />
                    Manage Locks — {managingGroup.name}
                  </h3>
                  <button onClick={() => setManagingGroup(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                    <X className="h-4 w-4 text-slate-500" />
                  </button>
                </div>
                <div className="p-4 border-b border-slate-100">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Filter locks..."
                      className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                      value={groupLockFilter}
                      onChange={(e) => setGroupLockFilter(e.target.value)}
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[55vh]">
                  {/* Locks in this group */}
                  {managingGroup.units && managingGroup.units.length > 0 && (
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">In this group</p>
                      {managingGroup.units
                        .filter(u => u.name.toLowerCase().includes(groupLockFilter.toLowerCase()) || u.mac_address?.toLowerCase().includes(groupLockFilter.toLowerCase()))
                        .map((unit) => (
                        <div key={unit.id} className="flex items-center justify-between py-2 px-3 mb-1 rounded-lg bg-purple-50 border border-purple-100">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{unit.name}</p>
                            <p className="text-xs text-slate-400 font-mono">{unit.mac_address}</p>
                          </div>
                          <button
                            onClick={async () => { await handleRemoveUnitFromGroup(unit.id); setManagingGroup(prev => prev ? {...prev, units: prev.units.filter(u => u.id !== unit.id)} : null); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <MinusCircle className="h-3.5 w-3.5" /> Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Available locks not in any group or in another group */}
                  <div className="px-4 pt-3 pb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Available locks (no group)</p>
                    {units
                      .filter(u => !u.group_id && (u.name.toLowerCase().includes(groupLockFilter.toLowerCase()) || u.mac_address?.toLowerCase().includes(groupLockFilter.toLowerCase())))
                      .map((unit) => (
                      <div key={unit.id} className="flex items-center justify-between py-2 px-3 mb-1 rounded-lg bg-slate-50 border border-slate-100">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{unit.name}</p>
                          <p className="text-xs text-slate-400 font-mono">{unit.mac_address}</p>
                        </div>
                        <button
                          onClick={async () => { await handleAddUnitToGroup(unit.id, managingGroup.id); setManagingGroup(prev => prev ? {...prev, units: [...prev.units, unit]} : null); }}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                        >
                          <ArrowRight className="h-3.5 w-3.5" /> Add
                        </button>
                      </div>
                    ))}
                    {units.filter(u => !u.group_id).length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-4">All locks are already in a group</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Units Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <Monitor className="h-5 w-5 mr-2 text-blue-500" />
                  Units ({units.length})
                </h2>
                <button
                  onClick={() => exportUnitsData(units)}
                  className="w-full sm:w-auto flex justify-center items-center px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              </div>
            </div>
            <div className="p-4 border-b border-slate-100 bg-white">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Filter units..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  value={unitFilter}
                  onChange={(e) => setUnitFilter(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('name', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Name <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('group_name', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Group <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('battery', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Battery <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('updated_at', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Update <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Shared With</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedAndFilteredUnits.map((unit) => (
                      <tr key={unit.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-slate-900">{unit.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {unit.group_name ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                              {unit.group_name}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${unit.battery < 20 ? 'bg-red-100 text-red-800' : unit.battery < 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {unit.battery}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">{format(new Date(unit.updated_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</td>
                        <td className="py-3 px-4 text-sm">
                          {unit.owner_id === user?.id ? (
                            <button
                              onClick={() => loadUnitMembers(unit)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Users className="h-3.5 w-3.5" />
                              Members
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              Shared with me
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex gap-1">
                            <button
                              onClick={() => setViewingDetailsUnit(unit)}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="View details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteUnit(unit.id, unit.name)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Unit"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Unit Members Modal */}
          {viewingMembersUnit && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewingMembersUnit(null)}>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-100 bg-blue-50/50 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    Shared With — {viewingMembersUnit.name}
                  </h3>
                  <button onClick={() => setViewingMembersUnit(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                    <X className="h-4 w-4 text-slate-500" />
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[60vh]">
                  {unitMembersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-blue-600" />
                    </div>
                  ) : unitMembers.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">
                      This unit is not shared with anyone
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {unitMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{member.user_full_name}</p>
                              <p className="text-xs text-slate-400">{member.email || 'No email'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {member.allow_settings_changes && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Settings
                              </span>
                            )}
                            <button
                              onClick={() => handleRevokeUnitMember(viewingMembersUnit, member)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Revoke access"
                            >
                              <MinusCircle size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Unit Details Modal */}
          {viewingDetailsUnit && (() => {
            const detailCoords = viewingDetailsUnit.coords ? JSON.parse(viewingDetailsUnit.coords) : null;
            return (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewingDetailsUnit(null)}>
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="px-6 py-4 border-b border-slate-100 bg-indigo-50/50 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-800 flex items-center">
                      <Monitor className="h-4 w-4 mr-2 text-indigo-500" />
                      {viewingDetailsUnit.name}
                    </h3>
                    <button onClick={() => setViewingDetailsUnit(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">MAC Address</span>
                      <span className="text-sm font-mono text-slate-800">{viewingDetailsUnit.mac_address}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Group</span>
                      <span className="text-sm font-medium text-slate-800">{viewingDetailsUnit.group_name || '—'}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Battery</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${viewingDetailsUnit.battery < 20 ? 'bg-red-100 text-red-800' : viewingDetailsUnit.battery < 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                        {viewingDetailsUnit.battery}%
                      </span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Temperature</span>
                      <span className="text-sm text-slate-800">{viewingDetailsUnit.temperature}°C</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Lock Mode</span>
                      <span className="text-sm text-slate-800">{formatLockMode(viewingDetailsUnit.accelerometer)}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Coordinates</span>
                      <span className="text-sm font-mono text-slate-800">
                        {detailCoords ? `${detailCoords.latitude.toFixed(4)}, ${detailCoords.longitude.toFixed(4)}` : 'N/A'}
                      </span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Container Type</span>
                      <span className="text-sm text-slate-800">{viewingDetailsUnit.container_type || '—'}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Last Update</span>
                      <span className="text-sm text-slate-800">{format(new Date(viewingDetailsUnit.updated_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</span>
                    </div>
                    {viewingDetailsUnit.description && (
                      <div className="px-6 py-3">
                        <span className="text-sm text-slate-500 block mb-1">Description</span>
                        <span className="text-sm text-slate-800">{viewingDetailsUnit.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Events Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                    <Activity className="h-5 w-5 mr-2 text-emerald-500" />
                    Events ({events.length})
                  </h2>
                  {selectedUnitId && (
                    <p className="text-sm text-slate-500 mt-1.5 flex items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2"></span>
                      Showing events for: <span className="font-medium text-slate-700 ml-1">{units.find(u => u.id === selectedUnitId)?.name}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => exportEventsData(events, units.find(u => u.id === selectedUnitId)?.name || 'selected unit')}
                    className="flex justify-center items-center px-4 py-2 text-sm font-medium bg-white text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors shadow-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                  <button
                    onClick={handleDeleteEvents}
                    className="flex justify-center items-center px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm hover:shadow"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Events
                  </button>
                </div>
              </div>
            </div>
            
            {/* Filters Section */}
            <div className="p-6 border-b border-slate-100">
              <div className="flex flex-col gap-6">
                {/* Date Filter */}
                <div className="w-full space-y-2">
                  <label className="text-sm font-semibold text-slate-600 ml-1">
                    Date Range Filter
                  </label>
                  <div className="bg-slate-50 p-2 rounded-xl border border-slate-200/60">
                    <DateFilterComponent
                      dateFilter={dateFilter}
                      onDateFilterChange={setDateFilter}
                    />
                  </div>
                </div>

                {/* Unit Selector */}
                <div className="w-full space-y-2">
                  <label className="text-sm font-semibold text-slate-600 ml-1">
                    Select Lock
                  </label>
                  <div className="relative">
                    <select
                      value={selectedUnitId}
                      onChange={(e) => {
                        setSelectedUnitId(e.target.value);
                      }}
                      className="w-full pl-4 pr-10 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 appearance-none shadow-sm transition-all"
                    >
                      <option value="">-- View all units or select one to delete --</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <ChevronRight className="h-4 w-4 rotate-90" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Filter */}
            <div className="px-6 py-4 border-b border-slate-100 bg-white">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Filter className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search specific events..."
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('registered_at', eventSortConfig, setEventSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Date <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <button onClick={() => requestSort('type', eventSortConfig, setEventSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Event Details <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" /></button>
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      User
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentEvents.map((event) => (
                    <tr key={event.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3.5 px-6 text-sm text-slate-500 whitespace-nowrap">{format(new Date(event.registered_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</td>
                      <td className="py-3.5 px-6 text-sm text-slate-700 font-medium">{getEventDescription(event)}</td>
                      <td className="py-3.5 px-6 text-sm text-slate-500">{getEventUserDisplay(event)}</td>
                    </tr>
                  ))}
                  {currentEvents.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-slate-500 text-sm">
                        No events found matching your criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                <span className="text-sm font-medium text-slate-500">
                  Showing <span className="text-slate-900">{startIndex + 1}</span> to <span className="text-slate-900">{Math.min(endIndex, sortedAndFilteredEvents.length)}</span> of <span className="text-slate-900">{sortedAndFilteredEvents.length}</span> entries
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1.5" />
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Export All Data */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-lg border border-slate-700 overflow-hidden">
            <div className="px-8 py-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-0">
                <div className="text-center sm:text-left">
                  <h3 className="text-xl font-bold text-white mb-1">Export Complete System Data</h3>
                  <p className="text-sm text-slate-300">Download a comprehensive Excel report including users, units, events, and groups</p>
                </div>
                <button
                  onClick={() => user && exportAllData(user, units, events, groups)}
                  className="w-full sm:w-auto flex justify-center items-center px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-50 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <Download className="h-5 w-5 mr-2 text-indigo-600" />
                  Generate Full Report
                </button>
              </div>
            </div>
          </div>
          </>)}
          {/* Footer */}
          <footer className="pt-4 pb-8 text-center space-y-2">
            <p className="text-xs text-slate-400 font-medium">
              LOCKWASTE Admin Portal &mdash; © {new Date().getFullYear()}. All rights reserved.
            </p>
            <p className="text-xs text-slate-400">
              Want to delete your account?{' '}
              <a href="/account-delete" className="underline hover:text-slate-400 transition-colors">
                Click here
              </a>
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
